// Visual tokens for the Traffic Control page (brief §4). Page-local `sx`, stock MUI theme, no createTheme.
import {alpha} from "@mui/material/styles";

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
export const TABULAR = {fontVariantNumeric: "tabular-nums"};

export const TYPE = {
    title: {fontSize: 18, fontWeight: 600, lineHeight: 1.2},
    eyebrow: {fontSize: 11, fontWeight: 600, color: "text.secondary", lineHeight: 1.2},
    kpiValue: {fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2, ...TABULAR},
    cell: {fontSize: 13, fontWeight: 400, lineHeight: 1.2},
    muted: {fontSize: 12.5, color: "text.secondary"},
    tag: {fontFamily: MONO, fontSize: 13, fontWeight: 600},
    chip: {fontSize: 10.5, fontWeight: 700, letterSpacing: "0.02em"},
    caption: {fontSize: 11.5, color: "text.secondary", lineHeight: 1.3},
};

export const NEUTRAL = {
    hairline: "rgba(0,0,0,.06)",
    divider: "rgba(0,0,0,.12)",
    headerBg: "#fafafa",
    hover: "rgba(25,118,210,.04)",
    selected: "rgba(25,118,210,.08)",
    selectedInset: "inset 3px 0 0 #1976d2",
    focusRing: "0 0 0 2px #fff, 0 0 0 4px #1976d2",
    idleText: "rgba(0,0,0,.46)",
    zeroDot: "rgba(0,0,0,.26)",
};

export const WARNING_TEXT = "#995400"; // 5.8:1 on white

/** Per-severity colours; functions take the theme so the values follow palette.* */
export const SEV = {
    error: {
        dot: (t) => t.palette.error.main,
        chipBg: (t) => alpha(t.palette.error.main, 0.10),
        chipText: (t) => t.palette.error.dark,
        text: (t) => t.palette.error.main,
        label: "error",
    },
    warning: {
        dot: (t) => t.palette.warning.main,
        chipBg: (t) => alpha(t.palette.warning.main, 0.12),
        chipText: () => WARNING_TEXT,
        text: () => WARNING_TEXT,
        label: "warning",
    },
    info: {
        dot: (t) => t.palette.grey[400],
        chipBg: () => "transparent",
        chipText: () => "rgba(0,0,0,.6)",
        chipBorder: "rgba(0,0,0,.23)",
        text: () => "rgba(0,0,0,.6)",
        label: "info",
    },
    idle: {
        dot: () => "transparent",
        chipBg: () => "transparent",
        chipText: () => NEUTRAL.idleText,
        chipBorder: "rgba(0,0,0,.16)",
        text: () => NEUTRAL.idleText,
        label: "idle",
    },
    ok: {
        dot: () => "transparent",
        chipBg: () => "transparent",
        chipText: () => "rgba(0,0,0,.6)",
        chipBorder: "rgba(0,0,0,.23)",
        text: (t) => t.palette.success.dark,
        label: "ok",
    },
};

/** Phone row left border per severity. */
export const PHONE_BAR = {
    error: (t) => t.palette.error.main,
    warning: (t) => t.palette.warning.main,
    info: (t) => t.palette.grey[400],
    idle: (t) => t.palette.grey[300],
    ok: (t) => t.palette.success.light,
};

export const HEAT = {
    low2: "rgba(211,47,47,.24)",
    low1: "rgba(211,47,47,.12)",
    high1: "rgba(46,125,50,.10)",
    high2: "rgba(46,125,50,.20)",
};

/** Heat tint for `value` against the tag's mean of earning days (null → no tint). */
export const heatFor = (value, mean) => {
    if (value === null || value === undefined || !mean || mean <= 0) return null;
    const r = Number(value) / mean;
    if (r < 0.5) return HEAT.low2;
    if (r < 0.8) return HEAT.low1;
    if (r > 2) return HEAT.high2;
    if (r > 1.3) return HEAT.high1;
    return null;
};

export const CHART = {
    bar: "#1976d2",
    barPrev: alpha("#1976d2", 0.5),
    zeroTick: "#e0e0e0",
    axisText: "rgba(0,0,0,.46)",
    gridLine: "rgba(0,0,0,.06)",
    line: "#757575", // theme.palette.grey[600]: coverage is context, not an alarm — keep orange for warnings only
};

export const CONTROL_H = 32;
export const ATTENTION_ROW_H = 44;
export const GRID_ROW_H = 32;
export const GRID_HEADER_H = 36;
